import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { hasActionAccess } from '../../../utils/roleBasedAccess';

const CandidateTable = ({ 
  candidates, 
  selectedCandidates, 
  onSelectionChange, 
  onCandidateClick, 
  onQuickAction,
  sortConfig,
  onSort,
  userRole
}) => {
  const [hoveredRow, setHoveredRow] = useState(null);

  const getStatusBadge = (status) => {
    const statusConfig = {
      'new': { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: 'Plus' },
      'screening': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: 'Clock' },
      'interview': { color: 'bg-purple-100 text-purple-800 border-purple-200', icon: 'Calendar' },
      'offer': { color: 'bg-green-100 text-green-800 border-green-200', icon: 'CheckCircle' },
      'rejected': { color: 'bg-red-100 text-red-800 border-red-200', icon: 'X' },
      'hired': { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: 'UserCheck' }
    };

    const config = statusConfig?.[status] || statusConfig?.['new'];
    
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${config?.color}`}>
        <Icon name={config?.icon} size={12} className="mr-1" />
        {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const getScoreBadge = (score) => {
    let colorClass = 'bg-gray-100 text-gray-800 border-gray-200';
    
    if (score >= 90) colorClass = 'bg-green-100 text-green-800 border-green-200';
    else if (score >= 80) colorClass = 'bg-blue-100 text-blue-800 border-blue-200';
    else if (score >= 70) colorClass = 'bg-yellow-100 text-yellow-800 border-yellow-200';
    else if (score >= 60) colorClass = 'bg-orange-100 text-orange-800 border-orange-200';
    else colorClass = 'bg-red-100 text-red-800 border-red-200';

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold border ${colorClass}`}>
        {score}%
      </span>
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(candidates?.map(c => c?.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectCandidate = (candidateId, checked) => {
    if (checked) {
      onSelectionChange([...selectedCandidates, candidateId]);
    } else {
      onSelectionChange(selectedCandidates?.filter(id => id !== candidateId));
    }
  };

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) return 'ArrowUpDown';
    return sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isAllSelected = candidates?.length > 0 && selectedCandidates?.length === candidates?.length;
  const isPartiallySelected = selectedCandidates?.length > 0 && selectedCandidates?.length < candidates?.length;

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="w-12 px-4 py-3">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isPartiallySelected}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                />
              </th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                <button
                  onClick={() => onSort('name')}
                  className="flex items-center space-x-1 hover:text-foreground transition-colors"
                >
                  <span>Candidate</span>
                  <Icon name={getSortIcon('name')} size={14} />
                </button>
              </th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                <button
                  onClick={() => onSort('position')}
                  className="flex items-center space-x-1 hover:text-foreground transition-colors"
                >
                  <span>Position Applied</span>
                  <Icon name={getSortIcon('position')} size={14} />
                </button>
              </th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                <button
                  onClick={() => onSort('aiScore')}
                  className="flex items-center space-x-1 hover:text-foreground transition-colors"
                >
                  <span>AI Score</span>
                  <Icon name={getSortIcon('aiScore')} size={14} />
                </button>
              </th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                <button
                  onClick={() => onSort('experience')}
                  className="flex items-center space-x-1 hover:text-foreground transition-colors"
                >
                  <span>Experience</span>
                  <Icon name={getSortIcon('experience')} size={14} />
                </button>
              </th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                <button
                  onClick={() => onSort('applicationDate')}
                  className="flex items-center space-x-1 hover:text-foreground transition-colors"
                >
                  <span>Applied Date</span>
                  <Icon name={getSortIcon('applicationDate')} size={14} />
                </button>
              </th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                <button
                  onClick={() => onSort('status')}
                  className="flex items-center space-x-1 hover:text-foreground transition-colors"
                >
                  <span>Status</span>
                  <Icon name={getSortIcon('status')} size={14} />
                </button>
              </th>
              <th className="text-center px-4 py-3 font-medium text-muted-foreground w-32">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {candidates?.map((candidate) => (
              <tr
                key={candidate?.id}
                className={`hover:bg-muted/30 transition-colors cursor-pointer ${
                  selectedCandidates?.includes(candidate?.id) ? 'bg-primary/5' : ''
                }`}
                onMouseEnter={() => setHoveredRow(candidate?.id)}
                onMouseLeave={() => setHoveredRow(null)}
                onClick={() => onCandidateClick(candidate)}
              >
                <td className="px-4 py-4" onClick={(e) => e?.stopPropagation()}>
                  <Checkbox
                    checked={selectedCandidates?.includes(candidate?.id)}
                    onChange={(e) => handleSelectCandidate(candidate?.id, e?.target?.checked)}
                  />
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-muted flex-shrink-0">
                      <Image
                        src={candidate?.avatar}
                        alt={candidate?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <div className="font-medium text-foreground">{candidate?.name}</div>
                      <div className="text-sm text-muted-foreground">{candidate?.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="font-medium text-foreground">{candidate?.position}</div>
                  <div className="text-sm text-muted-foreground">{candidate?.department}</div>
                </td>
                <td className="px-4 py-4">
                  {getScoreBadge(candidate?.aiScore)}
                </td>
                <td className="px-4 py-4">
                  <div className="text-foreground">{candidate?.experience} years</div>
                  <div className="text-sm text-muted-foreground">{candidate?.location}</div>
                </td>
                <td className="px-4 py-4 text-muted-foreground">
                  {formatDate(candidate?.applicationDate)}
                </td>
                <td className="px-4 py-4">
                  {getStatusBadge(candidate?.status)}
                </td>
                <td className="px-4 py-4" onClick={(e) => e?.stopPropagation()}>
                  <div className="flex items-center justify-center space-x-1">
                    {/* View Profile - Available to all roles */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onQuickAction('view', candidate)}
                      title="View Profile"
                    >
                      <Icon name="Eye" size={16} />
                    </Button>
                    
                    {/* Schedule Interview - Only for roles with interview scheduling access */}
                    {hasActionAccess(userRole, 'interviews', 'schedule') && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onQuickAction('interview', candidate)}
                        title="Schedule Interview"
                      >
                        <Icon name="Calendar" size={16} />
                      </Button>
                    )}
                    
                    {/* Send Message - Available to all roles except hr_user */}
                    {userRole !== 'hr_user' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onQuickAction('message', candidate)}
                        title="Send Message"
                      >
                        <Icon name="MessageCircle" size={16} />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {candidates?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No candidates found</h3>
          <p className="text-muted-foreground">
            Try adjusting your filters or search criteria to find candidates.
          </p>
        </div>
      )}
    </div>
  );
};

export default CandidateTable;